<template>
    <app-layout>
        <template #title>SaaS Metrics</template>
        <template #description
            >Revenue, customers, and growth analytics</template
        >

        <KioskNav />

        <div class="max-w-7xl px-4 py-8 sm:px-6 lg:px-8 mx-auto">
            <!-- Currency Selector & Sync Button -->
            <div class="mb-8 flex items-center justify-between">
                <div
                    class="rounded-lg bg-iron-100 p-1 dark:bg-iron-800 inline-flex items-center"
                >
                    <button
                        v-for="currency in currencies"
                        :key="currency"
                        @click="activeCurrency = currency"
                        :class="[
                            'px-4 py-2 text-sm font-medium rounded-md transition-all duration-200',
                            activeCurrency === currency
                                ? 'bg-white text-iron-900 shadow-sm dark:bg-iron-700 dark:text-white'
                                : 'text-iron-600 hover:text-iron-900 dark:text-iron-400 dark:hover:text-white',
                        ]"
                    >
                        {{ currency }}
                    </button>
                </div>

                <button
                    @click="syncMetrics"
                    :disabled="isSyncing"
                    class="gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-iron-900 text-white hover:bg-iron-800 dark:bg-iron-100 dark:text-iron-900 dark:hover:bg-iron-200 inline-flex items-center disabled:cursor-not-allowed disabled:opacity-50"
                >
                    <RefreshCw
                        :class="['h-4 w-4', isSyncing ? 'animate-spin' : '']"
                    />
                    {{ isSyncing ? "Syncing..." : "Sync Now" }}
                </button>
            </div>

            <Deferred data="currentMetrics">
                <template #fallback>
                    <div class="space-y-8">
                        <!-- Skeleton for hero metrics -->
                        <div class="gap-6 md:grid-cols-2 lg:grid-cols-4 grid">
                            <div
                                v-for="i in 4"
                                :key="i"
                                class="bg-white dark:bg-iron-800 rounded-xl p-6 border-iron-200 dark:border-iron-700 border"
                            >
                                <div
                                    class="h-3 w-20 bg-iron-200 dark:bg-iron-700 rounded animate-pulse mb-4"
                                ></div>
                                <div
                                    class="h-10 w-32 bg-iron-200 dark:bg-iron-700 rounded animate-pulse mb-2"
                                ></div>
                                <div
                                    class="h-3 w-24 bg-iron-200 dark:bg-iron-700 rounded animate-pulse"
                                ></div>
                            </div>
                        </div>
                    </div>
                </template>

                <div
                    v-if="getCurrentMetrics(activeCurrency)"
                    class="space-y-10"
                >
                    <!-- Hero Metrics -->
                    <section>
                        <div class="gap-6 md:grid-cols-2 lg:grid-cols-4 grid">
                            <!-- MRR -->
                            <div
                                class="from-blue-600 to-blue-700 rounded-xl p-6 text-white relative overflow-hidden bg-gradient-to-br"
                            >
                                <div
                                    class="top-0 right-0 w-32 h-32 bg-white/10 -mr-16 -mt-16 absolute rounded-full"
                                ></div>
                                <p
                                    class="text-blue-100 text-sm font-medium tracking-wider uppercase"
                                >
                                    MRR
                                </p>
                                <p class="text-3xl font-bold mt-2">
                                    {{
                                        formatCurrency(
                                            getCurrentMetrics(activeCurrency)
                                                ?.mrr ?? 0,
                                            activeCurrency,
                                        )
                                    }}
                                </p>
                                <p class="text-blue-200 text-sm mt-1">
                                    Current monthly recurring
                                </p>
                            </div>

                            <!-- ARR -->
                            <div
                                class="from-emerald-600 to-emerald-700 rounded-xl p-6 text-white relative overflow-hidden bg-gradient-to-br"
                            >
                                <div
                                    class="top-0 right-0 w-32 h-32 bg-white/10 -mr-16 -mt-16 absolute rounded-full"
                                ></div>
                                <p
                                    class="text-emerald-100 text-sm font-medium tracking-wider uppercase"
                                >
                                    ARR
                                </p>
                                <p class="text-3xl font-bold mt-2">
                                    {{
                                        formatCurrency(
                                            getCurrentMetrics(activeCurrency)
                                                ?.arr ?? 0,
                                            activeCurrency,
                                        )
                                    }}
                                </p>
                                <p class="text-emerald-200 text-sm mt-1">
                                    Annualized revenue
                                </p>
                            </div>

                            <!-- Total Customers -->
                            <div
                                class="from-violet-600 to-violet-700 rounded-xl p-6 text-white relative overflow-hidden bg-gradient-to-br"
                            >
                                <div
                                    class="top-0 right-0 w-32 h-32 bg-white/10 -mr-16 -mt-16 absolute rounded-full"
                                ></div>
                                <p
                                    class="text-violet-100 text-sm font-medium tracking-wider uppercase"
                                >
                                    Customers
                                </p>
                                <p class="text-3xl font-bold mt-2">
                                    {{
                                        getCurrentMetrics(activeCurrency)
                                            ?.total_customers ?? 0
                                    }}
                                </p>
                                <p class="text-violet-200 text-sm mt-1">
                                    Active subscriptions
                                </p>
                            </div>

                            <!-- Total Seats -->
                            <div
                                class="from-amber-500 to-amber-600 rounded-xl p-6 text-white relative overflow-hidden bg-gradient-to-br"
                            >
                                <div
                                    class="top-0 right-0 w-32 h-32 bg-white/10 -mr-16 -mt-16 absolute rounded-full"
                                ></div>
                                <p
                                    class="text-amber-100 text-sm font-medium tracking-wider uppercase"
                                >
                                    Seats
                                </p>
                                <p class="text-3xl font-bold mt-2">
                                    {{
                                        getCurrentMetrics(activeCurrency)
                                            ?.total_seats ?? 0
                                    }}
                                </p>
                                <p class="text-amber-200 text-sm mt-1">
                                    Paid user licenses
                                </p>
                            </div>
                        </div>
                    </section>

                    <!-- MRR Movement (This Month) -->
                    <section>
                        <div class="gap-3 mb-4 flex items-center">
                            <h2
                                class="text-lg font-semibold text-iron-900 dark:text-white"
                            >
                                MRR Movement
                            </h2>
                            <span
                                class="px-2.5 py-0.5 text-xs font-medium bg-iron-100 text-iron-600 dark:bg-iron-700 dark:text-iron-300 rounded-full"
                            >
                                This Month
                            </span>
                        </div>
                        <div class="gap-4 md:grid-cols-2 lg:grid-cols-5 grid">
                            <!-- Net New MRR -->
                            <div
                                class="bg-white dark:bg-iron-800 rounded-xl p-5 border-iron-200 dark:border-iron-700 border"
                            >
                                <div class="flex items-center justify-between">
                                    <p
                                        class="text-sm font-medium text-iron-500 dark:text-iron-400"
                                    >
                                        Net New MRR
                                    </p>
                                    <div
                                        v-if="
                                            (getCurrentMetrics(activeCurrency)
                                                ?.net_new_mrr ?? 0) > 0
                                        "
                                        class="p-1.5 bg-emerald-100 dark:bg-emerald-900/30 rounded-full"
                                    >
                                        <TrendingUp
                                            class="h-4 w-4 text-emerald-600 dark:text-emerald-400"
                                        />
                                    </div>
                                    <div
                                        v-else-if="
                                            (getCurrentMetrics(activeCurrency)
                                                ?.net_new_mrr ?? 0) < 0
                                        "
                                        class="p-1.5 bg-red-100 dark:bg-red-900/30 rounded-full"
                                    >
                                        <TrendingDown
                                            class="h-4 w-4 text-red-600 dark:text-red-400"
                                        />
                                    </div>
                                </div>
                                <p
                                    :class="[
                                        'text-2xl font-bold mt-2',
                                        (getCurrentMetrics(activeCurrency)
                                            ?.net_new_mrr ?? 0) > 0
                                            ? 'text-emerald-600 dark:text-emerald-400'
                                            : (getCurrentMetrics(activeCurrency)
                                                    ?.net_new_mrr ?? 0) < 0
                                              ? 'text-red-600 dark:text-red-400'
                                              : 'text-iron-900 dark:text-white',
                                    ]"
                                >
                                    {{
                                        formatCurrency(
                                            getCurrentMetrics(activeCurrency)
                                                ?.net_new_mrr ?? 0,
                                            activeCurrency,
                                        )
                                    }}
                                </p>
                            </div>

                            <!-- New MRR -->
                            <div
                                class="bg-white dark:bg-iron-800 rounded-xl p-5 border-l-emerald-500 border-iron-200 dark:border-iron-700 border border-l-4"
                            >
                                <p
                                    class="text-sm font-medium text-iron-500 dark:text-iron-400"
                                >
                                    New MRR
                                </p>
                                <p
                                    class="text-2xl font-bold mt-2 text-emerald-600 dark:text-emerald-400"
                                >
                                    +{{
                                        formatCurrency(
                                            getCurrentMetrics(activeCurrency)
                                                ?.new_mrr ?? 0,
                                            activeCurrency,
                                        )
                                    }}
                                </p>
                                <p class="text-xs text-iron-400 mt-1">
                                    From new customers
                                </p>
                            </div>

                            <!-- Expansion MRR -->
                            <div
                                class="bg-white dark:bg-iron-800 rounded-xl p-5 border-l-blue-500 border-iron-200 dark:border-iron-700 border border-l-4"
                            >
                                <p
                                    class="text-sm font-medium text-iron-500 dark:text-iron-400"
                                >
                                    Expansion MRR
                                </p>
                                <p
                                    class="text-2xl font-bold mt-2 text-blue-600 dark:text-blue-400"
                                >
                                    +{{
                                        formatCurrency(
                                            getCurrentMetrics(activeCurrency)
                                                ?.expansion_mrr ?? 0,
                                            activeCurrency,
                                        )
                                    }}
                                </p>
                                <p class="text-xs text-iron-400 mt-1">
                                    From added seats
                                </p>
                            </div>

                            <!-- Contraction MRR -->
                            <div
                                class="bg-white dark:bg-iron-800 rounded-xl p-5 border-l-orange-500 border-iron-200 dark:border-iron-700 border border-l-4"
                            >
                                <p
                                    class="text-sm font-medium text-iron-500 dark:text-iron-400"
                                >
                                    Contraction MRR
                                </p>
                                <p
                                    class="text-2xl font-bold mt-2 text-orange-600 dark:text-orange-400"
                                >
                                    -{{
                                        formatCurrency(
                                            getCurrentMetrics(activeCurrency)
                                                ?.contraction_mrr ?? 0,
                                            activeCurrency,
                                        )
                                    }}
                                </p>
                                <p class="text-xs text-iron-400 mt-1">
                                    From removed seats
                                </p>
                            </div>

                            <!-- Churned MRR -->
                            <div
                                class="bg-white dark:bg-iron-800 rounded-xl p-5 border-l-red-500 border-iron-200 dark:border-iron-700 border border-l-4"
                            >
                                <p
                                    class="text-sm font-medium text-iron-500 dark:text-iron-400"
                                >
                                    Churned MRR
                                </p>
                                <p
                                    class="text-2xl font-bold mt-2 text-red-600 dark:text-red-400"
                                >
                                    -{{
                                        formatCurrency(
                                            getCurrentMetrics(activeCurrency)
                                                ?.churned_mrr ?? 0,
                                            activeCurrency,
                                        )
                                    }}
                                </p>
                                <p class="text-xs text-iron-400 mt-1">
                                    From cancellations
                                </p>
                            </div>
                        </div>
                    </section>

                    <!-- Customer Activity -->
                    <section>
                        <div class="gap-3 mb-4 flex items-center">
                            <h2
                                class="text-lg font-semibold text-iron-900 dark:text-white"
                            >
                                Customer Activity
                            </h2>
                            <span
                                class="px-2.5 py-0.5 text-xs font-medium bg-iron-100 text-iron-600 dark:bg-iron-700 dark:text-iron-300 rounded-full"
                            >
                                This Month
                            </span>
                        </div>
                        <div class="gap-4 md:grid-cols-2 lg:grid-cols-4 grid">
                            <div
                                class="bg-white dark:bg-iron-800 rounded-xl p-5 border-iron-200 dark:border-iron-700 border"
                            >
                                <p
                                    class="text-sm font-medium text-iron-500 dark:text-iron-400"
                                >
                                    New Customers
                                </p>
                                <p
                                    class="text-2xl font-bold mt-2 text-emerald-600 dark:text-emerald-400"
                                >
                                    +{{
                                        getCurrentMetrics(activeCurrency)
                                            ?.new_customers ?? 0
                                    }}
                                </p>
                                <p class="text-xs text-iron-400 mt-1">
                                    Subscribed this month
                                </p>
                            </div>

                            <div
                                class="bg-white dark:bg-iron-800 rounded-xl p-5 border-iron-200 dark:border-iron-700 border"
                            >
                                <p
                                    class="text-sm font-medium text-iron-500 dark:text-iron-400"
                                >
                                    Churned Customers
                                </p>
                                <p
                                    class="text-2xl font-bold mt-2 text-red-600 dark:text-red-400"
                                >
                                    -{{
                                        getCurrentMetrics(activeCurrency)
                                            ?.churned_customers ?? 0
                                    }}
                                </p>
                                <p class="text-xs text-iron-400 mt-1">
                                    Canceled this month
                                </p>
                            </div>

                            <div
                                class="bg-white dark:bg-iron-800 rounded-xl p-5 border-iron-200 dark:border-iron-700 border"
                            >
                                <div class="gap-3 mb-0 flex items-center">
                                    <p
                                        class="text-sm font-medium text-iron-500 dark:text-iron-400"
                                    >
                                        ARPU
                                    </p>
                                    <span
                                        class="px-1.5 py-0.5 font-medium rounded bg-iron-100 text-iron-500 dark:bg-iron-700 dark:text-iron-400 text-[10px]"
                                        >Current</span
                                    >
                                </div>
                                <p
                                    class="text-2xl font-bold mt-2 text-iron-900 dark:text-white"
                                >
                                    {{
                                        formatCurrency(
                                            getCurrentMetrics(activeCurrency)
                                                ?.arpu ?? 0,
                                            activeCurrency,
                                        )
                                    }}
                                </p>
                                <p class="text-xs text-iron-400 mt-1">
                                    Avg revenue per user
                                </p>
                            </div>

                            <div
                                class="bg-white dark:bg-iron-800 rounded-xl p-5 border-iron-200 dark:border-iron-700 border"
                            >
                                <div class="gap-3 mb-0 flex items-center">
                                    <p
                                        class="text-sm font-medium text-iron-500 dark:text-iron-400"
                                    >
                                        LTV
                                    </p>
                                    <span
                                        class="px-1.5 py-0.5 font-medium rounded bg-iron-100 text-iron-500 dark:bg-iron-700 dark:text-iron-400 text-[10px]"
                                        >Calculated</span
                                    >
                                </div>
                                <p
                                    class="text-2xl font-bold mt-2 text-iron-900 dark:text-white"
                                >
                                    {{
                                        formatLtv(
                                            getCurrentMetrics(activeCurrency)
                                                ?.ltv ?? 0,
                                            activeCurrency,
                                        )
                                    }}
                                </p>
                                <p class="text-xs text-iron-400 mt-1">
                                    ARPU / Monthly Churn
                                </p>
                            </div>
                        </div>
                    </section>

                    <!-- Churn & Retention -->
                    <section>
                        <div class="gap-3 mb-4 flex items-center">
                            <h2
                                class="text-lg font-semibold text-iron-900 dark:text-white"
                            >
                                Churn & Retention
                            </h2>
                            <span
                                class="px-2.5 py-0.5 text-xs font-medium bg-iron-100 text-iron-600 dark:bg-iron-700 dark:text-iron-300 rounded-full"
                            >
                                Monthly Rate
                            </span>
                        </div>
                        <div class="gap-4 md:grid-cols-3 grid">
                            <div
                                class="bg-white dark:bg-iron-800 rounded-xl p-5 border-iron-200 dark:border-iron-700 border"
                            >
                                <p
                                    class="text-sm font-medium text-iron-500 dark:text-iron-400"
                                >
                                    Customer Churn Rate
                                </p>
                                <p
                                    :class="[
                                        'text-2xl font-bold mt-2',
                                        (getCurrentMetrics(activeCurrency)
                                            ?.customer_churn_rate ?? 0) > 5
                                            ? 'text-red-600 dark:text-red-400'
                                            : 'text-iron-900 dark:text-white',
                                    ]"
                                >
                                    {{
                                        formatPercent(
                                            getCurrentMetrics(activeCurrency)
                                                ?.customer_churn_rate ?? 0,
                                        )
                                    }}
                                </p>
                                <p class="text-xs text-iron-400 mt-1">
                                    Lost / Starting customers
                                </p>
                            </div>

                            <div
                                class="bg-white dark:bg-iron-800 rounded-xl p-5 border-iron-200 dark:border-iron-700 border"
                            >
                                <p
                                    class="text-sm font-medium text-iron-500 dark:text-iron-400"
                                >
                                    Revenue Churn Rate
                                </p>
                                <p
                                    :class="[
                                        'text-2xl font-bold mt-2',
                                        (getCurrentMetrics(activeCurrency)
                                            ?.revenue_churn_rate ?? 0) > 5
                                            ? 'text-red-600 dark:text-red-400'
                                            : 'text-iron-900 dark:text-white',
                                    ]"
                                >
                                    {{
                                        formatPercent(
                                            getCurrentMetrics(activeCurrency)
                                                ?.revenue_churn_rate ?? 0,
                                        )
                                    }}
                                </p>
                                <p class="text-xs text-iron-400 mt-1">
                                    Churned / Starting MRR
                                </p>
                            </div>

                            <div
                                class="bg-white dark:bg-iron-800 rounded-xl p-5 border-iron-200 dark:border-iron-700 border"
                            >
                                <p
                                    class="text-sm font-medium text-iron-500 dark:text-iron-400"
                                >
                                    Net Revenue Retention
                                </p>
                                <p
                                    :class="[
                                        'text-2xl font-bold mt-2',
                                        (getCurrentMetrics(activeCurrency)
                                            ?.net_revenue_retention ?? 0) >= 100
                                            ? 'text-emerald-600 dark:text-emerald-400'
                                            : 'text-iron-900 dark:text-white',
                                    ]"
                                >
                                    {{
                                        formatPercent(
                                            getCurrentMetrics(activeCurrency)
                                                ?.net_revenue_retention ?? 0,
                                        )
                                    }}
                                </p>
                                <p class="text-xs text-iron-400 mt-1">
                                    100%+ means net expansion
                                </p>
                            </div>
                        </div>
                    </section>
                </div>

                <div
                    v-else
                    class="py-16 text-iron-500 dark:text-iron-400 text-center"
                >
                    <p class="text-lg">No metrics data available</p>
                    <p class="text-sm mt-1">
                        Run the snapshot command to generate metrics
                    </p>
                </div>
            </Deferred>

            <!-- User & Platform Metrics (currency-agnostic) -->
            <Deferred data="currentMetrics">
                <template #fallback>
                    <div class="mt-10 gap-4 md:grid-cols-2 lg:grid-cols-4 grid">
                        <div
                            v-for="i in 4"
                            :key="i"
                            class="bg-white dark:bg-iron-800 rounded-xl p-6 border-iron-200 dark:border-iron-700 border"
                        >
                            <div
                                class="h-3 w-20 bg-iron-200 dark:bg-iron-700 rounded animate-pulse mb-4"
                            ></div>
                            <div
                                class="h-10 w-32 bg-iron-200 dark:bg-iron-700 rounded animate-pulse"
                            ></div>
                        </div>
                    </div>
                </template>

                <div v-if="aggregateMetrics" class="mt-10 space-y-10">
                    <!-- User Metrics -->
                    <section>
                        <div class="gap-3 mb-4 flex items-center">
                            <h2
                                class="text-lg font-semibold text-iron-900 dark:text-white"
                            >
                                Platform Users
                            </h2>
                            <span
                                class="px-2.5 py-0.5 text-xs font-medium bg-iron-100 text-iron-600 dark:bg-iron-700 dark:text-iron-300 rounded-full"
                            >
                                All Currencies
                            </span>
                        </div>
                        <div class="gap-4 md:grid-cols-2 lg:grid-cols-4 grid">
                            <div
                                class="bg-white dark:bg-iron-800 rounded-xl p-5 border-iron-200 dark:border-iron-700 border"
                            >
                                <div class="gap-3 mb-2 flex items-center">
                                    <p
                                        class="text-sm font-medium text-iron-500 dark:text-iron-400"
                                    >
                                        Total Users
                                    </p>
                                    <span
                                        class="px-1.5 py-0.5 font-medium rounded bg-iron-100 text-iron-500 dark:bg-iron-700 dark:text-iron-400 text-[10px]"
                                        >All Time</span
                                    >
                                </div>
                                <p
                                    class="text-2xl font-bold text-iron-900 dark:text-white"
                                >
                                    {{
                                        aggregateMetrics.total_users.toLocaleString()
                                    }}
                                </p>
                                <p class="text-xs text-iron-400 mt-1">
                                    Registered accounts
                                </p>
                            </div>

                            <div
                                class="bg-white dark:bg-iron-800 rounded-xl p-5 border-iron-200 dark:border-iron-700 border"
                            >
                                <div class="gap-3 mb-2 flex items-center">
                                    <p
                                        class="text-sm font-medium text-iron-500 dark:text-iron-400"
                                    >
                                        New Users
                                    </p>
                                    <span
                                        class="px-1.5 py-0.5 font-medium rounded bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400 text-[10px]"
                                        >This Month</span
                                    >
                                </div>
                                <p
                                    class="text-2xl font-bold text-emerald-600 dark:text-emerald-400"
                                >
                                    +{{
                                        aggregateMetrics.new_users.toLocaleString()
                                    }}
                                </p>
                                <p class="text-xs text-iron-400 mt-1">
                                    Signed up this month
                                </p>
                            </div>

                            <div
                                class="bg-white dark:bg-iron-800 rounded-xl p-5 border-iron-200 dark:border-iron-700 border"
                            >
                                <div class="gap-3 mb-2 flex items-center">
                                    <p
                                        class="text-sm font-medium text-iron-500 dark:text-iron-400"
                                    >
                                        Verified Users
                                    </p>
                                    <span
                                        class="px-1.5 py-0.5 font-medium rounded bg-iron-100 text-iron-500 dark:bg-iron-700 dark:text-iron-400 text-[10px]"
                                        >All Time</span
                                    >
                                </div>
                                <p
                                    class="text-2xl font-bold text-iron-900 dark:text-white"
                                >
                                    {{
                                        aggregateMetrics.verified_users.toLocaleString()
                                    }}
                                </p>
                                <p class="text-xs text-iron-400 mt-1">
                                    Email confirmed
                                </p>
                            </div>

                            <div
                                class="bg-white dark:bg-iron-800 rounded-xl p-5 border-iron-200 dark:border-iron-700 border"
                            >
                                <div class="gap-3 mb-2 flex items-center">
                                    <p
                                        class="text-sm font-medium text-iron-500 dark:text-iron-400"
                                    >
                                        Conversion Rate
                                    </p>
                                    <span
                                        class="px-1.5 py-0.5 font-medium rounded bg-iron-100 text-iron-500 dark:bg-iron-700 dark:text-iron-400 text-[10px]"
                                        >Overall</span
                                    >
                                </div>
                                <p
                                    class="text-2xl font-bold text-iron-900 dark:text-white"
                                >
                                    {{
                                        formatPercent(
                                            aggregateMetrics.overall_conversion_rate ??
                                                0,
                                        )
                                    }}
                                </p>
                                <p class="text-xs text-iron-400 mt-1">
                                    Users to paying customers
                                </p>
                            </div>
                        </div>
                    </section>

                    <!-- Reservation Metrics -->
                    <section>
                        <div class="gap-3 mb-4 flex items-center">
                            <h2
                                class="text-lg font-semibold text-iron-900 dark:text-white"
                            >
                                Reservations
                            </h2>
                        </div>
                        <div class="gap-4 md:grid-cols-2 lg:grid-cols-4 grid">
                            <div
                                class="bg-white dark:bg-iron-800 rounded-xl p-5 border-iron-200 dark:border-iron-700 border"
                            >
                                <div class="gap-3 mb-2 flex items-center">
                                    <p
                                        class="text-sm font-medium text-iron-500 dark:text-iron-400"
                                    >
                                        Total Reservations
                                    </p>
                                    <span
                                        class="px-1.5 py-0.5 font-medium rounded bg-iron-100 text-iron-500 dark:bg-iron-700 dark:text-iron-400 text-[10px]"
                                        >All Time</span
                                    >
                                </div>
                                <p
                                    class="text-2xl font-bold text-iron-900 dark:text-white"
                                >
                                    {{
                                        aggregateMetrics.total_reservations.toLocaleString()
                                    }}
                                </p>
                            </div>

                            <div
                                class="bg-white dark:bg-iron-800 rounded-xl p-5 border-iron-200 dark:border-iron-700 border"
                            >
                                <div class="gap-3 mb-2 flex items-center">
                                    <p
                                        class="text-sm font-medium text-iron-500 dark:text-iron-400"
                                    >
                                        New Reservations
                                    </p>
                                    <span
                                        class="px-1.5 py-0.5 font-medium rounded bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400 text-[10px]"
                                        >This Month</span
                                    >
                                </div>
                                <p
                                    class="text-2xl font-bold text-emerald-600 dark:text-emerald-400"
                                >
                                    +{{
                                        aggregateMetrics.new_reservations.toLocaleString()
                                    }}
                                </p>
                            </div>

                            <div
                                class="bg-white dark:bg-iron-800 rounded-xl p-5 border-iron-200 dark:border-iron-700 border"
                            >
                                <div class="gap-3 mb-2 flex items-center">
                                    <p
                                        class="text-sm font-medium text-iron-500 dark:text-iron-400"
                                    >
                                        Completed
                                    </p>
                                    <span
                                        class="px-1.5 py-0.5 font-medium rounded bg-iron-100 text-iron-500 dark:bg-iron-700 dark:text-iron-400 text-[10px]"
                                        >All Time</span
                                    >
                                </div>
                                <p
                                    class="text-2xl font-bold text-emerald-600 dark:text-emerald-400"
                                >
                                    {{
                                        aggregateMetrics.completed_reservations.toLocaleString()
                                    }}
                                </p>
                            </div>

                            <div
                                class="bg-white dark:bg-iron-800 rounded-xl p-5 border-iron-200 dark:border-iron-700 border"
                            >
                                <div class="gap-3 mb-2 flex items-center">
                                    <p
                                        class="text-sm font-medium text-iron-500 dark:text-iron-400"
                                    >
                                        Canceled
                                    </p>
                                    <span
                                        class="px-1.5 py-0.5 font-medium rounded bg-iron-100 text-iron-500 dark:bg-iron-700 dark:text-iron-400 text-[10px]"
                                        >All Time</span
                                    >
                                </div>
                                <p
                                    class="text-2xl font-bold text-red-600 dark:text-red-400"
                                >
                                    {{
                                        aggregateMetrics.canceled_reservations.toLocaleString()
                                    }}
                                </p>
                            </div>
                        </div>
                    </section>
                </div>
            </Deferred>

            <!-- Trend Charts -->
            <Deferred data="trends">
                <template #fallback>
                    <div class="mt-10 gap-6 lg:grid-cols-2 grid">
                        <div>
                            <h2
                                class="text-lg font-semibold text-iron-900 dark:text-white mb-4"
                            >
                                MRR Trend
                            </h2>
                            <div
                                class="bg-iron-100 dark:bg-iron-800 rounded-xl animate-pulse h-[350px]"
                            ></div>
                        </div>
                        <div>
                            <h2
                                class="text-lg font-semibold text-iron-900 dark:text-white mb-4"
                            >
                                Churn Rate Trend
                            </h2>
                            <div
                                class="bg-iron-100 dark:bg-iron-800 rounded-xl animate-pulse h-[350px]"
                            ></div>
                        </div>
                    </div>
                </template>

                <div
                    v-if="trends && trends.length > 0"
                    class="mt-10 gap-6 lg:grid-cols-2 grid"
                >
                    <!-- MRR Trend Line Chart -->
                    <div>
                        <div class="gap-3 mb-4 flex items-center">
                            <h2
                                class="text-lg font-semibold text-iron-900 dark:text-white"
                            >
                                MRR Trend (CZK)
                            </h2>
                            <span
                                class="px-2.5 py-0.5 text-xs font-medium bg-iron-100 text-iron-600 dark:bg-iron-700 dark:text-iron-300 rounded-full"
                            >
                                Last 12 Months
                            </span>
                        </div>
                        <div
                            class="bg-white dark:bg-iron-800 rounded-xl p-6 border-iron-200 dark:border-iron-700 border"
                        >
                            <LineChart
                                class="h-[300px]"
                                :data="mrrTrendChartData"
                                index="name"
                                :categories="['MRR']"
                                :colors="['#3B82F6']"
                                :y-formatter="formatChartCurrency"
                                :show-legend="false"
                            />
                        </div>
                    </div>

                    <!-- Churn Rate Trend Line Chart -->
                    <div>
                        <div class="gap-3 mb-4 flex items-center">
                            <h2
                                class="text-lg font-semibold text-iron-900 dark:text-white"
                            >
                                Churn Rate Trend (CZK)
                            </h2>
                            <span
                                class="px-2.5 py-0.5 text-xs font-medium bg-iron-100 text-iron-600 dark:bg-iron-700 dark:text-iron-300 rounded-full"
                            >
                                Last 12 Months
                            </span>
                        </div>
                        <div
                            class="bg-white dark:bg-iron-800 rounded-xl p-6 border-iron-200 dark:border-iron-700 border"
                        >
                            <LineChart
                                class="h-[300px]"
                                :data="churnTrendChartData"
                                index="name"
                                :categories="['Churn Rate']"
                                :colors="['#EF4444']"
                                :y-formatter="formatChartPercent"
                                :show-legend="false"
                            />
                        </div>
                    </div>
                </div>
            </Deferred>

            <!-- Customers Trend Bar Chart -->
            <Deferred data="trends">
                <template #fallback>
                    <div class="mt-6">
                        <h2
                            class="text-lg font-semibold text-iron-900 dark:text-white mb-4"
                        >
                            Customer Count
                        </h2>
                        <div
                            class="bg-iron-100 dark:bg-iron-800 rounded-xl animate-pulse h-[350px]"
                        ></div>
                    </div>
                </template>

                <div v-if="trends && trends.length > 0" class="mt-6">
                    <div class="gap-3 mb-4 flex items-center">
                        <h2
                            class="text-lg font-semibold text-iron-900 dark:text-white"
                        >
                            Customer Count (CZK)
                        </h2>
                        <span
                            class="px-2.5 py-0.5 text-xs font-medium bg-iron-100 text-iron-600 dark:bg-iron-700 dark:text-iron-300 rounded-full"
                        >
                            Last 12 Months
                        </span>
                    </div>
                    <div
                        class="bg-white dark:bg-iron-800 rounded-xl p-6 border-iron-200 dark:border-iron-700 border"
                    >
                        <LineChart
                            class="h-[300px]"
                            :data="customerTrendChartData"
                            index="name"
                            :categories="['Customers']"
                            :colors="['#8B5CF6']"
                            :show-legend="false"
                        />
                    </div>
                </div>
            </Deferred>
        </div>
    </app-layout>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { Deferred, router } from "@inertiajs/vue3";
import AppLayout from "../../../../Layouts/AppLayout.vue";
import KioskNav from "../components/KioskNav.vue";
import { LineChart } from "@/components/ui/chart-line";
import { TrendingUp, TrendingDown, RefreshCw } from "lucide-vue-next";

type MetricsData = {
    id: number;
    snapshot_date: string;
    currency: string;
    mrr: number;
    arr: number;
    new_mrr: number;
    churned_mrr: number;
    expansion_mrr: number;
    contraction_mrr: number;
    net_new_mrr: number;
    total_customers: number;
    total_seats: number;
    new_customers: number;
    churned_customers: number;
    trialing_customers: number;
    total_users: number;
    new_users: number;
    verified_users: number;
    customer_churn_rate: number;
    revenue_churn_rate: number;
    net_revenue_retention: number;
    arpu: number;
    ltv: number;
    trials_started: number;
    trials_converted: number;
    direct_conversions: number;
    trial_conversion_rate: number | null;
    overall_conversion_rate: number | null;
    total_reservations: number;
    new_reservations: number;
    completed_reservations: number;
    canceled_reservations: number;
    users_with_reservations: number;
};

const props = defineProps<{
    currentMetrics: {
        CZK: MetricsData[];
        EUR: MetricsData[];
        USD: MetricsData[];
    };
    trends: MetricsData[];
}>();

const currencies = ["CZK", "EUR", "USD"] as const;
type Currency = (typeof currencies)[number];

const activeCurrency = ref<Currency>("CZK");

function getCurrentMetrics(currency: Currency): MetricsData | null {
    const data = props.currentMetrics?.[currency];
    if (!data || data.length === 0) return null;
    return data[0];
}

const aggregateMetrics = computed(() => {
    // User and reservation metrics are the same across currencies, take from first available
    for (const currency of currencies) {
        const data = getCurrentMetrics(currency);
        if (data) return data;
    }
    return null;
});

const mrrTrendChartData = computed(() => {
    if (!props.trends || props.trends.length === 0) return [];

    // Filter only CZK data and group by snapshot_date
    return props.trends
        .filter((item) => item.currency === "CZK")
        .map((item) => ({
            name: item.snapshot_date.substring(0, 7), // YYYY-MM
            MRR: item.mrr / 100, // Convert cents to main currency
        }))
        .sort((a, b) => a.name.localeCompare(b.name));
});

const churnTrendChartData = computed(() => {
    if (!props.trends || props.trends.length === 0) return [];

    // Filter only CZK data
    return props.trends
        .filter((item) => item.currency === "CZK")
        .map((item) => ({
            name: item.snapshot_date.substring(0, 7), // YYYY-MM
            "Churn Rate": item.customer_churn_rate,
        }))
        .sort((a, b) => a.name.localeCompare(b.name));
});

const customerTrendChartData = computed(() => {
    if (!props.trends || props.trends.length === 0) return [];

    // Filter only CZK data
    return props.trends
        .filter((item) => item.currency === "CZK")
        .map((item) => ({
            name: item.snapshot_date.substring(0, 7), // YYYY-MM
            Customers: item.total_customers,
        }))
        .sort((a, b) => a.name.localeCompare(b.name));
});

function formatCurrency(value: number, currency: Currency): string {
    const amount = value / 100; // Convert from cents
    return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    }).format(amount);
}

function formatLtv(value: number, currency: Currency): string {
    // LTV is 0 when churn rate is 0 (to avoid infinity)
    // Show "∞" for infinite LTV (no churn)
    if (value === 0) {
        const currentMetrics = getCurrentMetrics(currency);
        // If churn rate is 0 or very small, show infinity symbol
        if ((currentMetrics?.customer_churn_rate ?? 0) < 0.01) {
            return "∞";
        }
    }
    return formatCurrency(value, currency);
}

function formatPercent(value: number): string {
    return `${value.toFixed(2)}%`;
}

function formatChartCurrency(tick: number): string {
    return String(Math.round(tick));
}

function formatChartPercent(tick: number): string {
    return `${tick.toFixed(1)}%`;
}

const isSyncing = ref(false);

function syncMetrics() {
    isSyncing.value = true;
    router.post(
        route("app.kiosk.synced-metrics.store"),
        {},
        {
            onFinish: () => {
                isSyncing.value = false;
            },
        },
    );
}
</script>
